import { useEffect, useState } from 'react';
import { Activity, Settings2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { findLesson } from '@/data/lessons';
import { findSoccerLesson } from '@/data/soccerLessons';
import { findBasketballLesson } from '@/data/basketballLessons';
import { findBaseballLesson } from '@/data/baseballLessons';

const FEED_TYPES = ['lesson_completed', 'streak', 'inactive'];

function lessonTitle(id?: string): string | null {
  if (!id) return null;
  return (findSoccerLesson(id) ?? findBasketballLesson(id) ?? findBaseballLesson(id) ?? findLesson(id))?.lesson.title ?? null;
}

type Item = { id: string; type: string; message: string; metadata: Record<string, unknown> | null; created_at: string };

function render(item: Item): string {
  if (item.type === 'lesson_completed') {
    const title = lessonTitle(item.metadata?.lesson_id as string | undefined);
    const name = item.message.split(' completed')[0];
    return title ? `${name} completed ${title} ✅` : item.message;
  }
  return item.message;
}

const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

type Prefs = { alert_completions: boolean; alert_streaks: boolean; alert_inactive: boolean; weekly_digest: boolean; email_reminders: boolean };
const DEFAULT_PREFS: Prefs = { alert_completions: true, alert_streaks: true, alert_inactive: true, weekly_digest: true, email_reminders: true };

export function TeamActivityFeed({ teamId }: { teamId?: string }) {
  const { user } = useAuth();
  const [items, setItems] = useState<Item[] | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>(DEFAULT_PREFS);

  useEffect(() => {
    if (!user) return;
    let q = supabase.from('coach_notifications')
      .select('id, type, message, metadata, created_at')
      .eq('coach_user_id', user.id).in('type', FEED_TYPES)
      .order('created_at', { ascending: false }).limit(15);
    if (teamId) q = q.eq('team_id', teamId);
    q.then(({ data }) => setItems((data as Item[]) ?? []));
    supabase.from('coach_settings' as never).select('*').eq('coach_user_id', user.id).maybeSingle()
      .then(({ data }) => { if (data) setPrefs({ ...DEFAULT_PREFS, ...(data as Partial<Prefs>) }); });
  }, [user, teamId]);

  const updatePref = async (key: keyof Prefs, value: boolean) => {
    if (!user) return;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    const { error } = await supabase.from('coach_settings' as never)
      .upsert({ coach_user_id: user.id, ...next, updated_at: new Date().toISOString() } as never, { onConflict: 'coach_user_id' });
    if (error) { setPrefs(prefs); toast.error('Could not save your setting'); } else toast.success('Setting saved');
  };

  const rows: [keyof Prefs, string][] = [
    ['alert_completions', 'Player completes an assigned lesson'],
    ['alert_streaks', 'Player reaches a 7-day streak'],
    ['alert_inactive', 'Player inactive for 7 days'],
    ['weekly_digest', 'Weekly digest email (Mondays)'],
    ['email_reminders', 'Reminder emails (setup help, check-ins)'],
  ];

  return (
    <div className="glass-card p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-sm flex items-center gap-2 text-foreground">
          <Activity className="w-4 h-4 text-primary" /> Team activity
        </h3>
        <Button variant="ghost" size="sm" onClick={() => setShowSettings(s => !s)} aria-expanded={showSettings} className="text-muted-foreground">
          <Settings2 className="w-4 h-4 mr-1" /> Alert settings
        </Button>
      </div>

      {showSettings && (
        <div className="rounded-xl bg-muted/30 p-3 space-y-2">
          {rows.map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-3 text-sm font-body">
              <span>{label}</span>
              <Switch checked={prefs[key]} onCheckedChange={v => updatePref(key, v)} aria-label={label} />
            </label>
          ))}
        </div>
      )}

      {items === null ? (
        <p className="text-xs text-muted-foreground font-body">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground font-body">
          No activity yet. When players finish assigned lessons, hit a 7-day streak, or go quiet for a week, it shows up here.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map(i => (
            <li key={i.id} className="flex items-start justify-between gap-3 text-sm font-body">
              <span className="text-foreground/90">{render(i)}</span>
              <span className="text-[11px] text-muted-foreground shrink-0">{ago(i.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
